import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MaterialSelect } from "@/components/slot/MaterialSelect";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { useLaminatyKDoske, useLaminovat } from "@/api/hooks/useLaminovanie";
import { cisloMm, sklonuj } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { Dielec } from "@/api/schemas";

/** Nadmiera prírezu laminátu — operácia Laminovanie v cenníku (KOLO 234). */
const NADMIERA = 20;

export interface LaminovatDialogProps {
  zakazkaId: string;
  variantaId: string;
  /** Dosky na laminovanie (viacvýber); prvá určuje odporúčaný laminát. */
  dielce: Dielec[];
  /** Materiál dosky prvého dielca — podľa jeho dekoru sa laminát navrhne. */
  doskaMaterialId: string | null;
  onClose: () => void;
}

/**
 * Okno Laminovať (KOLO 234). User: *„vyberieš laminát, aj ten bude
 * automaticky detekovaný, spýtaš sa, či na jednu alebo obe strany,
 * a laminátu dáš nadmieru +20 +20"*.
 *
 * Laminát v dekore dosky je predvybraný (prvý s jej štruktúrou), iný sa
 * vyberie z cenníka (kategória Laminát). Obe strany = dva riadky pod
 * doskou, každý sa dá potom vymeniť (protiťah).
 */
export function LaminovatDialog({
  zakazkaId,
  variantaId,
  dielce,
  doskaMaterialId,
  onClose,
}: LaminovatDialogProps): JSX.Element {
  const odporucane = useLaminatyKDoske(doskaMaterialId);
  const navrhy = odporucane.data?.data ?? [];
  const [materialId, setMaterialId] = useState<string | null>(null);
  const [strany, setStrany] = useState<1 | 2>(1);
  // Celý cenník laminátov až na požiadanie — inak by zoznam zakryl návrh
  const [inyLaminat, setInyLaminat] = useState(false);
  const mutacia = useLaminovat(zakazkaId, variantaId);

  // Automatický návrh — prvý laminát v dekore dosky, kým user nevyberie iný
  useEffect(() => {
    if (materialId === null && navrhy[0]) setMaterialId(navrhy[0].id);
  }, [navrhy, materialId]);

  const prvy = dielce[0];
  const x = prvy?.x_mm ? parseFloat(prvy.x_mm) : null;
  const y = prvy?.y_mm ? parseFloat(prvy.y_mm) : null;
  const kody = dielce.map((d) => d.kod ?? "?").join(", ");

  async function potvrd(): Promise<void> {
    if (!materialId) return;
    try {
      const r = await mutacia.mutateAsync({
        dielec_ids: dielce.map((d) => d.id),
        material_id: materialId,
        strany,
      });
      const n = r.data.vytvorene.length;
      toast({
        title: `Laminované: ${n} ${sklonuj(n, ["riadok laminátu", "riadky laminátu", "riadkov laminátu"])}`,
        description:
          r.data.preskocene.length > 0
            ? `Preskočené: ${r.data.preskocene.map((p) => `${p.kod} (${p.dovod})`).join(", ")}`
            : undefined,
      });
      onClose();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Laminovanie sa nedá",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Laminovať</DialogTitle>
          <DialogDescription>
            {dielce.length === 1
              ? `Doska ${kody}`
              : `${dielce.length} ${sklonuj(dielce.length, ["doska", "dosky", "dosiek"])}: ${kody}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Laminát</Label>
            {navrhy.length > 0 ? (
              <div className="space-y-1" role="radiogroup" aria-label="Odporúčané k doske">
                <p className="text-[11px] text-muted-foreground">
                  Odporúčané k doske — rovnaký dekor
                </p>
                {navrhy.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    role="radio"
                    aria-checked={materialId === m.id}
                    onClick={() => setMaterialId(m.id)}
                    className={cn(
                      "flex w-full items-baseline gap-2 rounded-md border px-3 py-1.5 text-left text-sm hover:bg-accent",
                      materialId === m.id && "border-vyber bg-vyber/10",
                    )}
                  >
                    <span className="font-mono text-xs">
                      {m.vlastny_id ?? m.demos_id ?? m.jafholz_id ?? ""}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{m.nazov}</span>
                    {m.x_mm && m.y_mm ? (
                      <span className="shrink-0 font-mono text-xs text-muted-foreground">
                        {cisloMm(m.x_mm)} × {cisloMm(m.y_mm)}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            ) : odporucane.isLoading ? (
              <p className="text-xs text-muted-foreground">Hľadám laminát v dekore dosky…</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                V dekore dosky laminát v cenníku nie je — vyber ho nižšie.
              </p>
            )}
            {navrhy.length > 0 && !inyLaminat ? (
              <button
                type="button"
                onClick={() => setInyLaminat(true)}
                className="text-xs text-primary hover:underline"
              >
                Iný laminát z cenníka…
              </button>
            ) : odporucane.isLoading ? null : (
              <MaterialSelect
                value={navrhy.some((m) => m.id === materialId) ? null : materialId}
                onChange={(id) => setMaterialId(id)}
                druh="plosny"
                sekcia="materialy"
                kategoriaIncludeCsv="LAMINAT"
                kompaktny
                autoFocus={inyLaminat}
                placeholder="Laminát z cenníka (dekor, kód)…"
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Strany</Label>
            <div className="flex gap-1 rounded-md border p-1 w-fit" role="radiogroup" aria-label="Strany">
              {(
                [
                  [1, "Jedna strana"],
                  [2, "Obe strany"],
                ] as const
              ).map(([n, text]) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={strany === n}
                  onClick={() => setStrany(n)}
                  className={cn(
                    "rounded px-3 py-1 text-xs transition-colors",
                    strany === n
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent",
                  )}
                >
                  {text}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {strany === 2
                ? "Dva riadky pod doskou (horná a spodná) — spodný laminát sa dá potom vymeniť (protiťah)."
                : "Jeden riadok laminátu pod doskou."}
            </p>
          </div>

          <p className="text-xs text-muted-foreground">
            Laminát dostane prírez +{NADMIERA} / +{NADMIERA}
            {x && y
              ? ` — ${prvy?.kod ?? ""} ${cisloMm(x)} × ${cisloMm(y)} → ${cisloMm(x + NADMIERA)} × ${cisloMm(y + NADMIERA)}`
              : ""}
            . Na pílu ani do Cut Rite nejde — Výstupy → Plán rezania laminátov.
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Zrušiť
          </Button>
          <Button
            type="button"
            disabled={!materialId || mutacia.isPending}
            onClick={() => void potvrd()}
          >
            {mutacia.isPending ? "Laminujem…" : "Laminovať"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Malé okno výberu laminátu (kategória Laminát) — výmena jednej strany. */
export function VyberLaminatuDialog({
  nadpis,
  onClose,
  onVyber,
}: {
  nadpis: string;
  onClose: () => void;
  onVyber: (materialId: string) => void;
}): JSX.Element {
  const [id, setId] = useState<string | null>(null);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{nadpis}</DialogTitle>
          <DialogDescription className="sr-only">
            Iný laminát z cenníka pre tento riadok.
          </DialogDescription>
        </DialogHeader>
        <MaterialSelect
          value={id}
          onChange={(v) => setId(v)}
          druh="plosny"
          sekcia="materialy"
          kategoriaIncludeCsv="LAMINAT"
          kompaktny
          autoFocus
          placeholder="Laminát z cenníka (dekor, kód)…"
        />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Zrušiť
          </Button>
          <Button type="button" disabled={!id} onClick={() => id && onVyber(id)}>
            Vymeniť
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
